import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { armarMailRadar } from "../../constants/plantillasRadar";
import { MESES_ES } from "../../utils/alertasRecurrencia";
import { getPaisCode } from "../../utils/getPaisCode";
import { connectGoogle, crearBorradorRadar } from "../../utils/googleOAuth";
import { log } from "../../utils/log";
import { registrarBorradorRadar } from "../../utils/radarBorradores";
import styles from "./stylesEscribirRadarModal.module.scss";

// Modal "Escribir" del Radar Comercial —
// docs/decisiones/2026-09-26-radar-mail-pagina-diseno.md §3, §9, §11-§13. Molde estándar del
// proyecto (`reactstrap` Modal/ModalHeader/ModalBody/ModalFooter, mismo patrón que
// `AgendarMovimientoModal`).
//
// SIN "agregar contacto" en esta fase (decisión explícita del prompt de Fase B): la lista de
// contactos es de solo selección (checkbox) sobre lo que ya trae `radarContactos.js` — sumar
// contactos nuevos y ofrecer registrarlos queda para el proyecto de Contactos aparte (§9 del
// diseño documenta por qué: el CRM hoy reescribe la tabla relacional completa en cada guardado,
// tocar eso de paso sería un riesgo mayor al que pide esta tarea).
//
// "Abrir en Gmail" NUNCA cambia el estado de la alerta — `escritoCliente`/`escritoAgencia` solo
// llegan, más tarde, cuando `gmail-sync` confirma un mail SALIENTE real (`radarMailSaliente.js`).
// El insert en
// `radar_borradores` de acá es puramente informativo (trazabilidad de intención) y nunca bloquea
// la creación del borrador si falla (10.170, ver `registrarBorradorRadar`).
//
// BORRADOR POR API DE GMAIL (2026-09-26, reemplaza las DOS vías por link que existieron antes):
//   - `?view=cm&...` (redacción "en línea"): Ignacio verificó en su Gmail real que NO trae la firma
//     predeterminada de la cuenta ni el estilo de texto predeterminado (Trebuchet en su caso).
//   - `mailto:` armado a mano (`https://mail.google.com/mail/?extsrc=mailto&url=...`): mismo
//     problema — Ignacio probó esta vía también y tampoco trae firma/tipografía reales.
// Ninguna de las dos podía llevar la firma real del vendedor porque un link no autentica nada del
// lado de Google — por eso el borrador pasa a crearse server-side vía `users.drafts.create` (edge
// function `radar-borrador`), que sí actúa CON el token OAuth del vendedor y puede leer su firma
// (`users.settings.sendAs`) y aplicar la tipografía institucional en el HTML del cuerpo.
//
// Deep link al borrador creado: `https://mail.google.com/mail/u/0/#drafts?compose=<draftId>` —
// VERIFICADO por Ignacio en la Parada 1 (2026-09-26, borrador real con su firma "Wecast -
// Ignacio" en Trebuchet azul): abre directo en modo edición. La otra variante probada
// (`#drafts/<threadId>`) queda descartada — documentada acá por si algún día `draftId` deja de
// venir en la respuesta y hay que volver a una alternativa.
export const EscribirRadarModal = ({
  isOpen,
  onClose,
  lado, // 'cliente' | 'agencia'
  alerta, // fila del Radar: empresaDisplay, empresaNorm, mesObjetivo, anoObjetivo, tipo, campana,
  // edificiosTotales, clientId
  contactos, // [{usuarioId, nombre, apellido, email, cargo}] — ya filtrados a con-email
  conteoRed, // { operativos, instalacion, piloto } de hoy (getConteoRedPorEstado) — null si no cargó
  hayAgenciaConEmail, // solo relevante si lado==='cliente' — dispara la frase de {frase_agencia}
  pais,
  usuarioId,
}) => {
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [estado, setEstado] = useState("idle"); // idle | creando | reauth | error

  // Al abrir, todos marcados por defecto (§3.2: "todos los contactos del lado van en Para").
  useEffect(() => {
    if (isOpen) {
      setSeleccionados(new Set(contactos.map((c) => c.usuarioId)));
      setEstado("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, alerta?.clientId, lado]);

  const mes = MESES_ES[alerta?.mesObjetivo] || "";

  // Greeting fijo al PRIMER contacto de la lista (no al primer marcado): la vista previa no
  // recalcula al tildar/destildar checkboxes — es un borrador, el vendedor ajusta el saludo en
  // Gmail si hace falta antes de mandar.
  const { asunto, cuerpoHtml } = useMemo(
    () =>
      armarMailRadar({
        lado,
        tipo: alerta?.tipo,
        empresa: alerta?.empresaDisplay,
        campana: alerta?.campana,
        mes,
        nEntonces: alerta?.edificiosTotales ?? null,
        conteoRed: conteoRed ?? null,
        nombreContacto: contactos[0]?.nombre,
        hayAgenciaConEmail,
      }),
    [lado, alerta, mes, conteoRed, contactos, hayAgenciaConEmail]
  );

  const toggleContacto = (usuarioId2) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(usuarioId2)) next.delete(usuarioId2);
      else next.add(usuarioId2);
      return next;
    });
  };

  const crearBorrador = async () => {
    const marcados = contactos.filter((c) => seleccionados.has(c.usuarioId));
    if (marcados.length === 0) return;

    setEstado("creando");
    try {
      const resultado = await crearBorradorRadar({
        usuario_id: usuarioId,
        pais: getPaisCode(pais),
        pais_nombre: pais,
        lado,
        client_id: alerta.clientId,
        empresa_norm: alerta.empresaNorm,
        mes: alerta.mesObjetivo,
        ano: alerta.anoObjetivo,
        to: marcados.map((c) => c.email).join(","),
        subject: asunto,
        html: cuerpoHtml,
      });

      registrarBorradorRadar({
        pais,
        empresaNorm: alerta.empresaNorm,
        mes: alerta.mesObjetivo,
        ano: alerta.anoObjetivo,
        lado,
        clientId: alerta.clientId,
        usuarioId,
      });

      const url = resultado.draftId
        ? `https://mail.google.com/mail/u/0/#drafts?compose=${resultado.draftId}`
        : "https://mail.google.com/mail/u/0/#drafts";
      window.open(url, "_blank", "noopener,noreferrer");
      onClose();
    } catch (err) {
      if (err.code === "REAUTH") {
        setEstado("reauth");
      } else {
        log.error("EscribirRadarModal.crearBorrador", err);
        setEstado("error");
      }
    }
  };

  // "vuelve al modal" (prompt): redirige de vuelta a la página actual (no al default
  // /admin/perfil) — el modal en sí no sobrevive el reload de un OAuth redirect completo, pero el
  // vendedor vuelve al Home con el Radar y puede reabrir el mismo sobre, ya reconectado.
  const reconectarGoogle = async () => {
    try {
      await connectGoogle(usuarioId, window.location.href, getPaisCode(pais));
    } catch {
      toast.error("Error al conectar con Google");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className={styles.dialog}>
      <ModalHeader toggle={onClose}>
        Escribir a {alerta?.empresaDisplay} · {lado === "agencia" ? "Agencia" : "Cliente"}
      </ModalHeader>
      <ModalBody>
        {estado === "reauth" ? (
          <div className={styles.reauth}>
            <p>Reconectá tu Google para crear borradores.</p>
            <p className={styles.reauthDetalle}>
              Tu conexión de Google todavía no tiene el permiso para crear borradores en Gmail — hace falta reconectar
              la cuenta una vez.
            </p>
          </div>
        ) : (
          <>
            {lado === "cliente" && hayAgenciaConEmail && !alerta?.escritoAgencia && (
              <div className={styles.recordatorioAgencia}>
                Todavía no le escribiste a la agencia. Conviene empezar por ahí.
              </div>
            )}
            <div className={styles.contactos}>
              {contactos.map((c) => (
                <label key={c.usuarioId} className={styles.contacto}>
                  <input
                    type="checkbox"
                    checked={seleccionados.has(c.usuarioId)}
                    onChange={() => toggleContacto(c.usuarioId)}
                  />
                  <span className={styles.contactoNombre}>
                    {c.nombre} {c.apellido}
                  </span>
                  {c.cargo && <span className={styles.contactoCargo}>{c.cargo}</span>}
                  <span className={styles.contactoEmail}>{c.email}</span>
                </label>
              ))}
            </div>
            <div className={styles.preview}>
              <div className={styles.previewAsunto}>
                <strong>Asunto:</strong> {asunto}
              </div>
              <div
                className={styles.previewCuerpo}
                style={{ fontFamily: "'Trebuchet MS',Helvetica,Arial,sans-serif", color: "#073763" }}
                dangerouslySetInnerHTML={{ __html: cuerpoHtml }}
              />
              <div className={styles.previewNota}>
                Tu firma de Gmail se agrega automáticamente al crear el borrador.
              </div>
            </div>
            {estado === "error" && (
              <div className={styles.errorMsg}>No se pudo crear el borrador. Probá de nuevo en un momento.</div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>
          Cancelar
        </Button>
        {estado === "reauth" ? (
          <Button color="primary" onClick={reconectarGoogle}>
            Reconectar Google
          </Button>
        ) : (
          <Button color="primary" onClick={crearBorrador} disabled={seleccionados.size === 0 || estado === "creando"}>
            {estado === "creando" ? "Creando…" : "Abrir en Gmail"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
